import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowCounterclockwise, Link45deg } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import useLandingStylingParams from '../../hooks/useLandingStylingParams';
import {
  applyLandingAccentOverride,
  clearLandingAccentOverride,
  DEFAULT_LANDING_ACCENT,
  findLandingPageRoot,
  getStoredLandingAccent,
  LANDING_ACCENT_PRESET_PURPLE,
  normalizeHex,
} from '../../utils/landingAccent';

const PRESETS = [
  { id: 'desk', label: 'Desk LED', hex: DEFAULT_LANDING_ACCENT },
  { id: 'purple', label: 'Purple', hex: LANDING_ACCENT_PRESET_PURPLE },
];

/**
 * Home-page header control: live-preview landing accent via CSS custom properties.
 * When ?styling=true, syncs accent hex to the URL for shareable preview links.
 */
export default function HomeAccentColorPicker() {
  const { isDark, themeName } = useTheme();
  const { accentFromUrl, setAccentParam, getShareUrl } = useLandingStylingParams();
  const [color, setColor] = useState(() => accentFromUrl || getStoredLandingAccent() || DEFAULT_LANDING_ACCENT);
  const [hexInput, setHexInput] = useState(() => accentFromUrl || getStoredLandingAccent() || DEFAULT_LANDING_ACCENT);
  const [open, setOpen] = useState(false);
  const [copyLabel, setCopyLabel] = useState('Copy share link');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (accentFromUrl) {
      setColor(accentFromUrl);
      setHexInput(accentFromUrl);
      return;
    }
    const stored = getStoredLandingAccent();
    if (stored) {
      setColor(stored);
      setHexInput(stored);
    }
  }, [accentFromUrl]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      const target = e.target;
      if (!wrapRef.current) return;
      if (!(target instanceof Node) || !wrapRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const shellBorder = isDark ? '1px solid rgba(255,255,255,0.16)' : '1px solid rgba(15,23,42,0.14)';
  const shellBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)';

  const applyColor = useCallback((hex, fromNode, { syncUrl = true } = {}) => {
    const normalized = normalizeHex(hex);
    if (!normalized) return;
    const root = findLandingPageRoot(fromNode);
    applyLandingAccentOverride(root, normalized);
    setColor(normalized);
    setHexInput(normalized);
    if (syncUrl) setAccentParam(normalized);
  }, [setAccentParam]);

  const handleColorChange = (e) => {
    applyColor(e.target.value, e.target);
  };

  const handleHexInputChange = (e) => {
    setHexInput(e.target.value);
  };

  const commitHexInput = () => {
    const normalized = normalizeHex(hexInput);
    if (normalized) {
      applyColor(normalized, wrapRef.current);
    } else {
      setHexInput(color);
    }
  };

  const handleHexKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitHexInput();
    }
  };

  const handleReset = (e) => {
    const root = findLandingPageRoot(e.currentTarget);
    clearLandingAccentOverride(root);
    setColor(DEFAULT_LANDING_ACCENT);
    setHexInput(DEFAULT_LANDING_ACCENT);
    setAccentParam(null);
    setOpen(false);
  };

  const handlePreset = (hex, e) => {
    applyColor(hex, e.currentTarget);
    setOpen(false);
  };

  const handleCopyShareLink = async () => {
    const url = getShareUrl({ accent: color, theme: themeName });
    try {
      await navigator.clipboard.writeText(url);
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Copy share link'), 2000);
    } catch {
      setCopyLabel('Copy failed');
      setTimeout(() => setCopyLabel('Copy share link'), 2000);
    }
  };

  return (
    <div className="landing-accent-picker-wrap" ref={wrapRef}>
      <button
        type="button"
        className="landing-accent-picker-trigger"
        aria-label="Accent color"
        aria-expanded={open}
        title="Accent color — try teal, purple, or any custom color"
        style={{ border: shellBorder, background: shellBg }}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'; }}
      >
        <span className="landing-accent-picker-swatch" style={{ background: color }} aria-hidden />
      </button>

      {open && (
        <div
          className="landing-accent-picker-popover"
          role="dialog"
          aria-label="Choose accent color"
        >
          <div className="landing-accent-picker-popover-row">
            <label className="landing-accent-picker-native-label">
              Custom
              <input
                type="color"
                className="landing-accent-picker-native"
                value={color}
                onChange={handleColorChange}
                aria-label="Custom accent color"
              />
            </label>
            <button
              type="button"
              className="landing-accent-picker-reset"
              onClick={handleReset}
              title="Reset to default"
              aria-label="Reset accent to default"
            >
              <ArrowCounterclockwise size={14} aria-hidden />
            </button>
          </div>
          <label className="landing-accent-picker-hex-label">
            Hex
            <input
              type="text"
              className="landing-accent-picker-hex-input"
              value={hexInput}
              onChange={handleHexInputChange}
              onBlur={commitHexInput}
              onKeyDown={handleHexKeyDown}
              spellCheck={false}
              autoComplete="off"
              aria-label="Accent color hex value"
            />
          </label>
          <div className="landing-accent-picker-presets" role="list" aria-label="Accent presets">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="listitem"
                className={`landing-accent-picker-preset${color === preset.hex ? ' landing-accent-picker-preset--active' : ''}`}
                onClick={(e) => handlePreset(preset.hex, e)}
                title={preset.label}
              >
                <span className="landing-accent-picker-preset-swatch" style={{ background: preset.hex }} />
                <span>{preset.label}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="landing-accent-picker-share"
            onClick={handleCopyShareLink}
          >
            <Link45deg size={14} aria-hidden />
            {copyLabel}
          </button>
        </div>
      )}
    </div>
  );
}
